// Chemin : C:\gestion-copro\src\components\chat\BoutonDictee.tsx
// Session 137 : Dictée vocale via Web Speech Recognition API
// Zéro coût, zéro backend — API native du navigateur
// Compatible Chrome, Edge, Safari (pas Firefox)
// Session 182 : Gestion explicite des erreurs (permission micro, navigateur incompatible)
// Session 280 : Timeout silence 5s + relance auto si onend prématuré (comportement Chrome mobile)

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, MicOff, AlertCircle } from 'lucide-react';

interface BoutonDicteeProps {
  onTranscript: (text: string) => void;
  onInterim?: (text: string) => void;
  disabled?: boolean;
}

// Durée de silence avant arrêt réel (ms) — ne démarre qu'après le premier mot détecté
const SILENCE_TIMEOUT_MS = 5000;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getSpeechRecognition = (): (new () => any) | null => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
};

export default function BoutonDictee({ onTranscript, onInterim, disabled = false }: BoutonDicteeProps) {
  const [isListening, setIsListening] = useState(false);
  const [erreurMessage, setErreurMessage] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recognitionRef = useRef<any>(null);
  const erreurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Flag interne : arrêt volontaire vs onend prématuré du navigateur
  const shouldBeListeningRef = useRef(false);
  // Flag : au moins un transcript reçu (on ne démarre le timer qu'après)
  const hasSpokenRef = useRef(false);

  const isSupported = typeof window !== 'undefined' && getSpeechRecognition() !== null;

  const afficherErreur = useCallback((message: string) => {
    setErreurMessage(message);
    if (erreurTimerRef.current) clearTimeout(erreurTimerRef.current);
    erreurTimerRef.current = setTimeout(() => setErreurMessage(null), 5000);
  }, []);

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  // stopListening : arrêt volontaire — on pose le flag pour que onend ne relance pas
  const stopListening = useCallback(() => {
    shouldBeListeningRef.current = false;
    hasSpokenRef.current = false;
    clearSilenceTimer();
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
  }, [clearSilenceTimer]);

  // Lancer/relancer la reconnaissance (mutualisé entre start et relance auto)
  const startRecognition = useCallback(() => {
    const SpeechRecognitionAPI = getSpeechRecognition();
    if (!SpeechRecognitionAPI) return;

    const recognition = new SpeechRecognitionAPI();
    recognition.lang = 'fr-FR';
    recognition.continuous = true;
    recognition.interimResults = true;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    recognition.onresult = (event: any) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      if (interimTranscript && onInterim) {
        onInterim(interimTranscript);
      }

      if (finalTranscript.trim()) {
        onTranscript(finalTranscript.trim());

        // Premier mot détecté : activer le timer de silence
        hasSpokenRef.current = true;
        clearSilenceTimer();
        silenceTimerRef.current = setTimeout(() => {
          stopListening();
        }, SILENCE_TIMEOUT_MS);
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    recognition.onerror = (event: any) => {
      console.error('Erreur dictée vocale:', event.error);

      switch (event.error) {
        case 'not-allowed':
          shouldBeListeningRef.current = false;
          hasSpokenRef.current = false;
          clearSilenceTimer();
          setIsListening(false);
          afficherErreur(
            'Accès au micro refusé. Cliquez sur le cadenas à gauche de l\'URL → Autorisations → Micro → Autoriser, puis rechargez la page.'
          );
          break;
        case 'audio-capture':
          shouldBeListeningRef.current = false;
          hasSpokenRef.current = false;
          clearSilenceTimer();
          setIsListening(false);
          afficherErreur(
            'Aucun micro détecté. Vérifiez qu\'un micro est branché et activé sur votre ordinateur.'
          );
          break;
        case 'network':
          shouldBeListeningRef.current = false;
          hasSpokenRef.current = false;
          clearSilenceTimer();
          setIsListening(false);
          afficherErreur(
            'Problème de connexion. La dictée vocale nécessite une connexion internet active.'
          );
          break;
        case 'no-speech':
        case 'aborted':
          // Pas d'erreur affichée — onend gérera la relance si nécessaire
          break;
        default:
          afficherErreur(
            `La dictée vocale a rencontré un problème (${event.error}). Essayez avec Chrome ou Edge.`
          );
      }
    };

    recognition.onend = () => {
      // Si l'arrêt est volontaire (shouldBeListening = false) → on s'arrête vraiment
      // Sinon → le navigateur a coupé prématurément → on relance
      if (shouldBeListeningRef.current) {
        try {
          recognition.start();
        } catch {
          // Si la relance échoue, on recrée une instance propre
          setTimeout(() => {
            if (shouldBeListeningRef.current) startRecognition();
          }, 100);
        }
      } else {
        setIsListening(false);
      }
    };

    recognitionRef.current = recognition;

    try {
      recognition.start();
    } catch (e) {
      console.error('Impossible de démarrer la dictée:', e);
      shouldBeListeningRef.current = false;
      setIsListening(false);
      afficherErreur(
        'Impossible de démarrer la dictée. Vérifiez que votre navigateur autorise l\'accès au micro.'
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onTranscript, onInterim, afficherErreur, clearSilenceTimer, stopListening]);

  const startListening = useCallback(() => {
    setErreurMessage(null);
    shouldBeListeningRef.current = true;
    hasSpokenRef.current = false;
    setIsListening(true);
    startRecognition();
  }, [startRecognition]);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  // Nettoyage à la destruction du composant
  useEffect(() => {
    return () => {
      shouldBeListeningRef.current = false;
      if (recognitionRef.current) recognitionRef.current.abort();
      if (erreurTimerRef.current) clearTimeout(erreurTimerRef.current);
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    };
  }, []);

  if (!isSupported) return null;

  return (
    <div className="flex items-center gap-1 flex-shrink-0 relative">
      {/* Message d'erreur */}
      {erreurMessage && (
        <div className="absolute bottom-full mb-2 right-0 w-72 p-3 bg-amber-50 border border-amber-300 rounded-lg shadow-lg z-50">
          <div className="flex gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 leading-relaxed">{erreurMessage}</p>
          </div>
          <button
            onClick={() => setErreurMessage(null)}
            className="absolute top-1 right-2 text-amber-400 hover:text-amber-600 text-sm"
          >
            ×
          </button>
        </div>
      )}

      {/* Indicateur ENREGISTREMENT EN COURS */}
      {isListening && (
        <div className="flex items-center gap-1.5 px-2.5 py-1 bg-red-50 border border-red-200 rounded-lg animate-pulse">
          <div className="w-2 h-2 bg-red-500 rounded-full" />
          <span className="text-xs font-medium text-red-600 whitespace-nowrap">Dictée en cours</span>
        </div>
      )}

      {/* Bouton micro */}
      <button
        onClick={toggleListening}
        disabled={disabled}
        className={`p-2.5 rounded-xl transition-colors ${
          isListening
            ? 'bg-red-100 text-red-600 hover:bg-red-200'
            : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
        } disabled:opacity-50`}
        title={isListening ? 'Arrêter la dictée' : 'Dicter un message (arrêt auto après 5s de silence)'}
      >
        {isListening ? (
          <MicOff className="w-5 h-5" />
        ) : (
          <Mic className="w-5 h-5" />
        )}
      </button>
    </div>
  );
}
